/* eslint-disable no-unused-vars */
import React from "react";
import moment from "moment";
import "moment/locale/fr";
import { CircularProgress } from "@mui/material";
import { capitalizeFirstLetter } from "../../Utils/Function";
import { DV2 } from "../DeclarationV2/theme";
import { Avatar, Card, FieldError, useDv2Theme } from "../DeclarationV2/layout/Ui";

interface Props {
    teachr: any;
    /** Réception du fichier choisi, même signature que l'ancien `PhotoSection` (`getFile` de ProfilStep). */
    onImageUpload: (value: File | null, error: boolean) => void;
    loading?: boolean;
    errorMessage?: string | null;
}

/** « 25 ans, membre depuis juillet 2025, abdoulaye.s@teachr.fr » */
export const profileHeaderSubtitle = (teachr: any): string => {
    const parts: string[] = [];
    if (teachr?.birthday) parts.push(`${moment().diff(moment(teachr.birthday), "years")} ans`);
    if (teachr?.creationDatetime) parts.push(`membre depuis ${moment(teachr.creationDatetime).locale("fr").format("MMMM YYYY")}`);
    if (teachr?.mail) parts.push(String(teachr.mail).toLowerCase());
    return parts.join(", ");
};

/**
 * En-tête de l'onglet Profil des paramètres repensés (maquette 8a) : la photo
 * ou les initiales, le nom, l'âge, l'ancienneté et l'e-mail sur une ligne, et
 * « Changer la photo ». Même envoi de la photo que l'ancien `PhotoSection`,
 * qui reste dans le code pour l'ancienne page.
 */
const ProfileHeaderV2 = ({ teachr, onImageUpload, loading = false, errorMessage }: Props) => {
    const { primary, fontFamily } = useDv2Theme();
    const name = `${capitalizeFirstLetter(teachr?.firstName ?? "")} ${capitalizeFirstLetter(teachr?.lastName ?? "")}`.trim();
    const initials = `${(teachr?.firstName ?? "").charAt(0)}${(teachr?.lastName ?? "").charAt(0)}`.toUpperCase() || "?";
    const picture: string | undefined = teachr?.profilePicture?.contentUrl || undefined;
    const subtitle = profileHeaderSubtitle(teachr);

    return (
        <Card as="section" aria-label="Votre profil" style={{ padding: 22, fontFamily }}>
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5">
                <div style={{ width: 72, height: 72, flexShrink: 0, position: "relative" }}>
                    {picture ? (
                        <img src={picture} alt="" style={{ width: 72, height: 72, borderRadius: "50%", objectFit: "cover", border: `1px solid ${DV2.border}` }} />
                    ) : (
                        <Avatar text={initials} size={72} />
                    )}
                    {loading && (
                        <div aria-label="Envoi de la photo" style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "rgba(255,255,255,.7)", display: "grid", placeItems: "center" }}>
                            <CircularProgress size={28} />
                        </div>
                    )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <h2 style={{ fontSize: 22, fontWeight: 800, margin: 0, lineHeight: 1.2 }}>{name}</h2>
                    {subtitle && <div style={{ fontSize: 14, color: DV2.muted, marginTop: 2, overflowWrap: "anywhere" }}>{subtitle}</div>}
                    {errorMessage && <FieldError>{errorMessage}</FieldError>}
                </div>
                <div className="shrink-0">
                    {/* Même contrôle que l'ancien bouton (UploadButton) : une image, sinon erreur « Mauvais format ». */}
                    <label style={{ display: "inline-flex", alignItems: "center", padding: "10px 16px", borderRadius: 12, border: "1px solid #C9D3E0", background: "#fff", color: primary, fontWeight: 800, fontSize: 13, fontFamily, cursor: loading ? "default" : "pointer", whiteSpace: "nowrap" }}>
                        <input
                            type="file"
                            accept="image/*"
                            hidden
                            disabled={loading}
                            aria-label="Changer la photo de profil"
                            onChange={(e) => {
                                const file = e.currentTarget.files?.[0] ?? null;
                                e.currentTarget.value = "";
                                if (!file) return;
                                onImageUpload(file.type.startsWith("image") ? file : null, !file.type.startsWith("image"));
                            }}
                        />
                        {loading ? "Chargement…" : "Changer la photo"}
                    </label>
                </div>
            </div>
        </Card>
    );
};

export default ProfileHeaderV2;
